import React, { useState } from 'react';
import { 
    User, Shield, FileClock, Building2, IdCard, LogOut, 
    Mail, Lock, History, Filter, Search, 
    AlertCircle, CheckCircle, Clock, Smartphone, Monitor,
} from 'lucide-react';

const UserCenterView = ({ currentUser, onLogout }) => {
    const [activeTab, setActiveTab] = useState('profile');

    const tabs = [
        { id: 'profile', label: '个人资料', icon: User },
        { id: 'security', label: '账号安全', icon: Shield },
        { id: 'logs', label: '操作日志', icon: FileClock },
    ];

    // Default fallback if currentUser is missing or incomplete
    const user = currentUser || {
        name: '未知用户',
        id: 'Unknown',
        base: '未知部门',
        phone: 'N/A',
        email: 'N/A',
        role: '访客',
        status: 'N/A'
    };

    return (
        <div className="space-y-6">
            {/* Header Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-8 flex items-center gap-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-32 bg-blue-50 rounded-full blur-3xl opacity-50 -mr-16 -mt-16"></div>
                <div className="w-24 h-24 rounded-full bg-slate-200 border-4 border-white shadow-lg flex items-center justify-center text-3xl font-bold text-slate-500 z-10">
                    {user.name ? user.name[0].toUpperCase() : 'U'}
                </div>
                <div className="flex-1 z-10">
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">欢迎回来，{user.name}</h2>
                    <div className="flex gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><Building2 size={14}/> {user.base}</span>
                        <span className="flex items-center gap-1"><IdCard size={14}/> 工号: {user.id}</span>
                    </div>
                </div>
                <div className="z-10">
                     <button onClick={onLogout} className="px-6 py-2.5 bg-white border border-red-200 text-red-600 rounded-lg hover:bg-red-50 font-medium shadow-sm transition-colors flex items-center gap-2">
                        <LogOut size={16}/> 退出登录
                     </button>
                </div>
            </div>

            <div className="grid grid-cols-4 gap-6 min-h-[500px]">
                {/* Sidebar Navigation */}
                <div className="col-span-1 bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden h-fit">
                    <div className="p-4 border-b border-slate-100 font-bold text-slate-700">账号中心</div>
                    <nav className="p-2">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors mb-1 ${activeTab === tab.id ? 'bg-blue-50 text-blue-600' : 'text-slate-600 hover:bg-slate-50'}`}
                            >
                                <tab.icon size={18} />
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Content Area */}
                <div className="col-span-3 bg-white rounded-xl shadow-sm border border-slate-100 p-8">
                    {activeTab === 'profile' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                            <h3 className="text-lg font-bold text-slate-800 mb-6 border-l-4 border-blue-600 pl-3">基本资料</h3>
                            <div className="grid grid-cols-2 gap-8">
                                <div>
                                    <label className="block text-sm font-medium text-slate-500 mb-2">用户姓名</label>
                                    <input type="text" value={user.name} disabled className="w-full border border-slate-200 bg-slate-50 rounded-lg px-4 py-2.5 text-slate-700"/>
                                </div>
                                 <div>
                                    <label className="block text-sm font-medium text-slate-500 mb-2">所属部门/基地</label>
                                    <input type="text" value={user.base} disabled className="w-full border border-slate-200 bg-slate-50 rounded-lg px-4 py-2.5 text-slate-700"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-500 mb-2">手机号码</label>
                                    <div className="flex gap-2">
                                        <input type="tel" value={user.phone} disabled className="flex-1 border border-slate-200 bg-slate-50 rounded-lg px-4 py-2.5 text-slate-700"/>
                                        <button className="text-blue-600 text-sm font-medium hover:underline">修改</button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-500 mb-2">电子邮箱</label>
                                    <div className="flex gap-2">
                                         <div className="flex-1 border border-slate-200 bg-slate-50 rounded-lg px-4 py-2.5 text-slate-700 flex items-center gap-2">
                                            <Mail size={16} className="text-slate-400"/>
                                            <span>{user.email || `${user.id}@example.com`}</span>
                                         </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="pt-8 border-t border-slate-100 mt-8">
                                <h3 className="text-lg font-bold text-slate-800 mb-6 border-l-4 border-blue-600 pl-3">岗位信息</h3>
                                <div className="bg-blue-50 rounded-xl p-6 border border-blue-100 flex items-start gap-4">
                                    <div className="p-3 bg-white rounded-lg shadow-sm text-blue-600">
                                        <IdCard size={24}/>
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-blue-800 text-lg mb-1">{user.role}</h4>
                                        <p className="text-sm text-blue-600 mb-2">状态: {user.status}</p>
                                        <p className="text-xs text-blue-500 leading-relaxed max-w-xl">
                                            当前用户拥有系统访问权限，可进行业务操作。
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'security' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                            <h3 className="text-lg font-bold text-slate-800 mb-6 border-l-4 border-blue-600 pl-3">账号安全</h3>
                            
                            <div className="space-y-4">
                                <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                                    <CheckCircle size={20} className="text-green-600 mt-0.5"/>
                                    <div>
                                        <h4 className="font-medium text-green-800">账号安全状态良好</h4>
                                        <p className="text-sm text-green-600 mt-1">您的账号安全评分为 90 分，建议定期更新密码</p>
                                    </div>
                                </div>

                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-blue-100 rounded-lg">
                                                <Lock size={20} className="text-blue-600"/>
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-slate-800">登录密码</h4>
                                                <p className="text-xs text-slate-500">建议定期更换密码</p>
                                            </div>
                                        </div>
                                        <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
                                            修改密码
                                        </button>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                                        <p>上次修改时间: 2025-12-15 14:30</p>
                                    </div>
                                </div>

                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-purple-100 rounded-lg">
                                                <Smartphone size={20} className="text-purple-600"/>
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-slate-800">手机绑定</h4>
                                                <p className="text-xs text-slate-500">用于接收验证码和重要通知</p>
                                            </div>
                                        </div>
                                        <button className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors">
                                            已绑定
                                        </button>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                                        <p>已绑定手机: 138****8000</p>
                                    </div>
                                </div>

                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-green-100 rounded-lg">
                                                <Mail size={20} className="text-green-600"/>
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-slate-800">邮箱绑定</h4>
                                                <p className="text-xs text-slate-500">用于接收重要通知和找回密码</p>
                                            </div>
                                        </div>
                                        <button className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors">
                                            已绑定
                                        </button>
                                    </div>
                                    <div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600">
                                        <p>已绑定邮箱: user@example.com</p>
                                    </div>
                                </div>

                                <div className="border border-slate-200 rounded-xl p-5">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-orange-100 rounded-lg">
                                                <Monitor size={20} className="text-orange-600"/>
                                            </div>
                                            <div>
                                                <h4 className="font-medium text-slate-800">登录设备管理</h4>
                                                <p className="text-xs text-slate-500">管理已登录的设备</p>
                                            </div>
                                        </div>
                                        <button className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm hover:bg-slate-50 transition-colors">
                                            查看设备
                                        </button>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <Monitor size={16} className="text-slate-400"/>
                                                <div>
                                                    <p className="text-sm text-slate-700">Windows PC - Chrome</p>
                                                    <p className="text-xs text-slate-500">浙江省杭州市 · 192.168.1.100</p>
                                                </div>
                                            </div>
                                            <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded">当前设备</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {activeTab === 'logs' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
                            <h3 className="text-lg font-bold text-slate-800 mb-6 border-l-4 border-blue-600 pl-3">操作日志</h3>
                            
                            <div className="flex items-center gap-4 mb-6">
                                <div className="flex-1 relative">
                                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
                                    <input type="text" placeholder="搜索操作内容..." className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"/>
                                </div>
                                <button className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 transition-colors">
                                    <Filter size={16}/>
                                    筛选
                                </button>
                                <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 transition-colors">
                                    <History size={16}/>
                                    导出日志
                                </button>
                            </div>

                            <div className="border border-slate-200 rounded-xl overflow-hidden">
                                <table className="w-full">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                        <tr>
                                            <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">操作时间</th>
                                            <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">操作类型</th>
                                            <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">操作内容</th>
                                            <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">IP地址</th>
                                            <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">状态</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        <tr className="hover:bg-slate-50">
                                            <td className="px-4 py-3 text-sm text-slate-700">2025-12-20 14:32:15</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                                    <Clock size={12}/>
                                                    登录
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700">用户登录系统</td>
                                            <td className="px-4 py-3 text-sm text-slate-600">192.168.1.100</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                                    <CheckCircle size={14}/>
                                                    成功
                                                </span>
                                            </td>
                                        </tr>
                                        <tr className="hover:bg-slate-50">
                                            <td className="px-4 py-3 text-sm text-slate-700">2025-12-20 14:28:42</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">
                                                    <FileClock size={12}/>
                                                    查询
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700">查看采购订单列表</td>
                                            <td className="px-4 py-3 text-sm text-slate-600">192.168.1.100</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                                    <CheckCircle size={14}/>
                                                    成功
                                                </span>
                                            </td>
                                        </tr>
                                        <tr className="hover:bg-slate-50">
                                            <td className="px-4 py-3 text-sm text-slate-700">2025-12-20 14:25:18</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                                                    <User size={12}/>
                                                    创建
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700">创建采购订单 PO20251220001</td>
                                            <td className="px-4 py-3 text-sm text-slate-600">192.168.1.100</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                                    <CheckCircle size={14}/>
                                                    成功
                                                </span>
                                            </td>
                                        </tr>
                                        <tr className="hover:bg-slate-50">
                                            <td className="px-4 py-3 text-sm text-slate-700">2025-12-20 14:20:05</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs">
                                                    <Shield size={12}/>
                                                    修改
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700">修改个人资料信息</td>
                                            <td className="px-4 py-3 text-sm text-slate-600">192.168.1.100</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                                    <CheckCircle size={14}/>
                                                    成功
                                                </span>
                                            </td>
                                        </tr>
                                        <tr className="hover:bg-slate-50">
                                            <td className="px-4 py-3 text-sm text-slate-700">2025-12-20 14:15:33</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                                    <Clock size={12}/>
                                                    登录
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700">用户登录系统</td>
                                            <td className="px-4 py-3 text-sm text-slate-600">192.168.1.100</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 text-green-600 text-sm">
                                                    <CheckCircle size={14}/>
                                                    成功
                                                </span>
                                            </td>
                                        </tr>
                                        <tr className="hover:bg-slate-50">
                                            <td className="px-4 py-3 text-sm text-slate-700">2025-12-20 14:12:20</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded text-xs">
                                                    <AlertCircle size={12}/>
                                                    异常
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-700">密码错误登录失败</td>
                                            <td className="px-4 py-3 text-sm text-slate-600">192.168.1.100</td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center gap-1 text-red-600 text-sm">
                                                    <AlertCircle size={14}/>
                                                    失败
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex items-center justify-between">
                                <p className="text-sm text-slate-500">显示 1-6 条，共 128 条记录</p>
                                <div className="flex items-center gap-2">
                                    <button className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50" disabled>上一页</button>
                                    <button className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm">1</button>
                                    <button className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">2</button>
                                    <button className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">3</button>
                                    <span className="text-slate-400">...</span>
                                    <button className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">22</button>
                                    <button className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">下一页</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserCenterView;